import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPhoneCall, FiMail, FiMapPin } from 'react-icons/fi';

import { CONTACT_INFO, FOOTER_LINK_GROUPS } from '../../constants';
import { Container } from '../ui';
import { Logo } from '../../../shared/components';
import SocialLinks from './SocialLinks';

/**
 * Site footer.
 *
 * Read off Figma node 16383:21316 (1920x441). Colours come from the file's
 * variables rather than the exported hex, which is stale in places:
 *   ground      #333643  (gray/860)
 *   headings    12px bold, 16px leading, uppercase, #99a1af (gray/500)
 *   links       15px regular white, 22.5px leading, 14px apart
 *   contact     16px regular white (the address bold), rows 52px apart,
 *               a 20px icon 8px from the text
 *   tagline     15px white, 394px wide, 32px from the logo and the socials
 *   social      four marks, 22px, 17px apart
 *   rule        1px #162447 -- blue/900, and all but invisible on the footer's
 *               own grey. That is the design: the line is drawn, and at this
 *               contrast it reads as a seam rather than a rule.
 *
 * Horizontally the design places the row 64px from the right edge and lets it
 * overhang the page grid on the left. That is loose placement, not a second
 * grid, so the row sits in the usual `Container` and keeps the design's own
 * split instead: the brand block takes 697/1701 of the width and the three
 * columns share the rest, spread edge to edge.
 *
 * The brand block comes first in the source, so in Arabic it lands on the
 * right where the design puts it, and in English it moves to the left without
 * any reordering.
 */

const ColumnHeading = ({ children }) => (
    <h2 className="tw-text-[12px] tw-font-bold tw-uppercase tw-leading-4 tw-text-v2-neutral-400">
        {children}
    </h2>
);

/** One contact row: a 20px icon, then the value 8px away. */
const ContactRow = ({ icon: Icon, bold, children }) => (
    <li className="tw-flex tw-items-center tw-gap-2">
        <Icon className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-white" aria-hidden />
        <span className={`tw-text-[16px] tw-leading-normal tw-text-white ${bold ? 'tw-font-bold' : ''}`}>
            {children}
        </span>
    </li>
);

const Footer = () => {
    const { t } = useTranslation();
    const year = new Date().getFullYear();

    return (
        <footer className="tw-bg-v2-footer tw-text-white">
            <Container className="tw-pt-16 tw-pb-10 lg:tw-pt-[134px] lg:tw-pb-[26px]">
                <div className="tw-flex tw-flex-col tw-gap-12 lg:tw-flex-row lg:tw-items-start lg:tw-gap-0">
                    {/* 697 of the design's 1701 -- the brand block's share. */}
                    <div className="tw-flex tw-flex-col tw-items-start tw-gap-8 lg:tw-w-[41%] lg:tw-shrink-0">
                        <Logo tone="dark" />

                        <p className="tw-max-w-[394px] tw-text-[15px] tw-font-normal tw-leading-[22.5px] tw-text-white">
                            {t('v2.footer.tagline')}
                        </p>

                        <SocialLinks variant="bare" />
                    </div>

                    {/* The remaining 1004: three columns spread edge to edge. */}
                    <div className="tw-grid tw-grid-cols-2 tw-gap-10 sm:tw-grid-cols-3 lg:tw-flex lg:tw-w-[59%] lg:tw-justify-between lg:tw-gap-6">
                        {FOOTER_LINK_GROUPS.map(group => (
                            <nav key={group.key} aria-label={t(group.titleKey)} className="tw-flex tw-flex-col tw-gap-5">
                                <ColumnHeading>{t(group.titleKey)}</ColumnHeading>

                                <ul className="tw-flex tw-flex-col tw-gap-[14px]">
                                    {group.items.map(item => (
                                        <li key={item.key}>
                                            <Link
                                                to={item.to}
                                                className="tw-text-[15px] tw-font-normal tw-leading-[22.5px] tw-text-white hover:tw-text-v2-brand-ring tw-transition-colors"
                                            >
                                                {t(item.labelKey)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        ))}

                        {/* The full row on a phone, where the grid is two
                            columns and this would otherwise get one of them.
                            A contact value is an indivisible token — a number,
                            an address — and in 155px "+974 3366-3877" broke at
                            its hyphen and "info@learning-go.com" after its own,
                            each landing as two lines that read as two values. */}
                        <div className="tw-col-span-2 tw-flex tw-flex-col tw-gap-5 sm:tw-col-span-1">
                            <ColumnHeading>{t('v2.footer.contact')}</ColumnHeading>

                            <ul className="tw-flex tw-flex-col tw-gap-[27px]">
                                {CONTACT_INFO.phones.map(phone => (
                                    <ContactRow key={phone.key} icon={FiPhoneCall}>
                                        <span>
                                            {t(phone.labelKey)}:{' '}
                                            {/* Latin digits keep their own direction inside Arabic text. */}
                                            <a href={`tel:${phone.number.replace(/[\s-]/g, '')}`} dir="ltr" className="tw-whitespace-nowrap hover:tw-text-v2-brand-ring">
                                                {phone.number}
                                            </a>
                                        </span>
                                    </ContactRow>
                                ))}

                                <ContactRow icon={FiMail} bold>
                                    {/* `break-all` rather than `nowrap`: an
                                        address can be longer than any column,
                                        so it has to be allowed to wrap — just
                                        not at the hyphen, which reads as the
                                        end of the address. */}
                                    <a href={`mailto:${CONTACT_INFO.email}`} dir="ltr" className="tw-break-all hover:tw-text-v2-brand-ring">
                                        {CONTACT_INFO.email}
                                    </a>
                                </ContactRow>

                                <ContactRow icon={FiMapPin}>
                                    <span>
                                        {t('v2.footer.location')}: {t(CONTACT_INFO.address.countryCityKey)}
                                    </span>
                                </ContactRow>
                            </ul>
                        </div>
                    </div>
                </div>
            </Container>

            <div className="tw-border-t tw-border-v2-footer-rule">
                <p className="tw-px-5 tw-pt-4 tw-pb-[22px] tw-text-center tw-text-[14px] tw-leading-5 tw-text-white">
                    {t('v2.footer.rights', { year })}
                </p>
            </div>
        </footer>
    );
};

export default Footer;
